import React from "react"
import {compose} from "redux"
import {useTranslation} from "react-i18next"

// HOCs
import withConclusion, {WithConclusionProps} from "../../../hoc/withConclusion"
import withInterpretation, {WithInterpretationProps} from "../../../hoc/withInterpretation"

// Components
import Kbd from "../../../../../components/modern/Kbd"
import StatementSuggestions from "../StatementSuggestions"
import StatementPalette from "../StatementPalette"
import LeadChips from "../LeadChips"

// Statements
import {
    appendLead,
    EcgStatement,
    insertStatement,
    LeadLine,
    leadLineAt,
    lineAt,
    matchStatements,
    setLeads,
    StatementMatch,
    statementLanguage,
} from "../ecgStatements"

// Lines
import {insertLine, removeLine} from "./lines"

// Constants
import {CONCLUSION_MAX_LENGTH} from "../../../constants"

// Styles
import useStyles from "./styles"

const AUTOSAVE_DELAY = 900;

/** Removals kept for undo per record — bounded, so a long review cannot grow it forever. */
const UNDO_DEPTH = 100;

/** A statement taken out with its remove button, and the line it was on. */
type Removal = {text: string, index: number}

/**
 * One statement's place in the field: its line in the text, the band it
 * covers (all its wrapped lines), and where its remove button sits — just
 * after the end of its text.
 */
type LineMark = {index: number, top: number, bottom: number, left: number, buttonTop: number}


const OPTIONS: {phraseKey: string, shortKey: string, urgent?: boolean}[] = [
    {phraseKey: "interpretation.1", shortKey: "1"},
    {phraseKey: "interpretation.2", shortKey: "2"},
    {phraseKey: "interpretation.1_teen", shortKey: "6"},
    {phraseKey: "interpretation.3", shortKey: "3"},
    {phraseKey: "interpretation.4", shortKey: "4", urgent: true},
    {phraseKey: "interpretation.5", shortKey: "5"},
];

/** Imperative surface the page uses for the Ctrl+S and 1–6 shortcuts. */
export type ModernDispoApi = {
    saveNow(): void
    pick(index: number): void
}

type ModernDispoCardOwnProps = {
    editable: boolean
    /**
     * Identity of the record currently under review. Approving with "receive
     * next" swaps the record without unmounting this card, so the draft has to
     * be discarded on that transition — see the sync effect below.
     */
    recordId: string | null
    apiRef: React.MutableRefObject<ModernDispoApi | null>
    /**
     * Page-owned cache of the in-progress draft, so it survives the
     * unmount/remount that toggling the sidebar causes — the two variants live
     * at different points in the tree. Without it the remount re-seeded from a
     * store that still held the previous conclusion.
     */
    draftCacheRef?: React.MutableRefObject<{recordId: string | null, text: string} | null>
    /** Reports "has unsaved or in-flight conclusion text" up to the page, which gates Approve. */
    onBusyChange?(busy: boolean): void
    /**
     * Reports that the findings field wants room for a whole report.
     *
     * The page owns the height cap on the info band, so growing the textarea
     * without telling it would only make the card scroll internally.
     */
    onExpandChange?(expanded: boolean): void
    /**
     * "card" is the third column of the top info strip; "dock" is the
     * two-column strip under the trace in sidebar layout. Same state, same
     * autosave — only the arrangement differs.
     */
    variant?: "card" | "dock"
    mobile?: boolean
}

type ModernDispoCardProps = ModernDispoCardOwnProps & WithConclusionProps & WithInterpretationProps

/**
 * Free-text findings + a one-press conclusion picker.
 *
 * Naming follows the design prototype and the screens signed off from it: the
 * textarea is headed INTERPRETATION and the chips are headed CONCLUSION. The
 * redux fields underneath are unchanged — the textarea still reads and writes
 * `conclusion`, the chips still read and write `interpretation` — so nothing
 * about what is stored or sent moved; only the visible headings do.
 *
 * The textarea autosaves 900 ms after the last keystroke and flushes on
 * unmount, because flipping the skin toggle unmounts this tree mid-review and
 * an unsaved draft would otherwise be lost. It never dispatches
 * `toggleConclusionEditing` — that flag is shared redux state gating Classic's
 * Approve button.
 *
 * @param props
 */
function ModernDispoCard(props: ModernDispoCardProps) {
    const styles = useStyles();
    const [t, i18n] = useTranslation("common");
    const {
        editable,
        recordId,
        apiRef,
        draftCacheRef,
        onBusyChange,
        onExpandChange,
        variant,
        mobile = false,
        conclusion,
        updateConclusion,
        updatingConclusion,
        interpretation,
        updateInterpretation,
    } = props;

    const isDock = variant === "dock";

    // A cached draft only applies to the record it was typed against, and only
    // while it still differs from the store — once the store has caught up it is
    // no longer "unsaved", it is just the value.
    const cached = draftCacheRef
      && draftCacheRef.current
      && draftCacheRef.current.recordId === recordId
      && draftCacheRef.current.text !== (conclusion || "")
        ? draftCacheRef.current
        : null;

    const [draft, setDraft] = React.useState<string>(cached ? cached.text : (conclusion || ""));
    const [dirty, setDirty] = React.useState(cached !== null);
    const [interpretationDraft, setInterpretationDraft] = React.useState<string>(interpretation || "");

    /*
     * Statement assistance for the findings box.
     *
     * `suggestions` is what drops under the field while typing; `paletteOpen`
     * is the browsable list. `dismissedLine` remembers the line Escape was
     * pressed on, so the suggestions stay shut until that line actually
     * changes — otherwise every further keystroke reopens what was just
     * dismissed.
     */
    const [suggestions, setSuggestions] = React.useState<StatementMatch[]>([]);
    const [activeSuggestion, setActiveSuggestion] = React.useState(0);
    const [paletteOpen, setPaletteOpen] = React.useState(false);
    /**
     * The findings field grown in place to show the whole report.
     *
     * Deliberately not a dialog: reading the findings back is a checking act —
     * "did I say ST elevation in the right leads?" — and that question needs
     * the trace, which anything covering the screen takes away.
     */
    const [expanded, setExpanded] = React.useState(false);
    /** The lead-slot line the caret is on, or null when it is not on one. */
    const [leadLine, setLeadLine] = React.useState<LeadLine | null>(null);

    /*
     * Remove and undo.
     *
     * Each statement line gets a remove button at its end, laid over the
     * textarea, which itself stays exactly as it was. A removal is kept here
     * so Undo (the button, or Ctrl+Z) can put that line back where it was.
     * The list belongs to the open record only and is never sent.
     */
    const historyRef = React.useRef<Removal[]>([]);
    const [canUndo, setCanUndo] = React.useState(false);
    /**
     * Whether the last change in the box was a removal. Ctrl+Z inside the box
     * undoes the removal only then; after typing it is the browser's own undo
     * of the typing, as it always was.
     */
    const lastWasRemovalRef = React.useRef(false);
    const [lineMarks, setLineMarks] = React.useState<LineMark[]>([]);
    const [hoverLine, setHoverLine] = React.useState<number | null>(null);
    const mirrorRef = React.useRef<HTMLDivElement | null>(null);
    const expandRef = React.useRef<HTMLButtonElement | null>(null);
    const dismissedLineRef = React.useRef<string | null>(null);
    /**
     * Per-instance, because the card and the dock can both be mounted and two
     * listboxes sharing an id would point every `aria-controls` at the first.
     */
    const SUGGESTIONS_ID = React.useRef(
        "dispo-statements-" + Math.random().toString(36).slice(2, 8),
    ).current;
    /** Caret to restore after React has painted an inserted statement. */
    const pendingCaretRef = React.useRef<number | null>(null);
    const language = statementLanguage(i18n.language);

    const timerRef = React.useRef<number | undefined>(undefined);
    const draftRef = React.useRef<string>(cached ? cached.text : (conclusion || ""));
    // Seeding this true is what stops the mount effect below from overwriting a
    // restored draft with the store's stale value.
    const dirtyRef = React.useRef(cached !== null);
    const editableRef = React.useRef(editable);
    const updateConclusionRef = React.useRef(updateConclusion);
    const recordIdRef = React.useRef(recordId);
    const interpretationDraftRef = React.useRef<string>(interpretationDraft);
    const interpretationRef = React.useRef<string>(interpretation || "");
    const updateInterpretationRef = React.useRef(updateInterpretation);

    editableRef.current = editable;
    updateConclusionRef.current = updateConclusion;
    interpretationDraftRef.current = interpretationDraft;
    interpretationRef.current = interpretation || "";
    updateInterpretationRef.current = updateInterpretation;

    const writeCache = React.useCallback((text: string | null) => {
        if (!draftCacheRef) {
            return
        }

        draftCacheRef.current = text === null ? null : {recordId: recordIdRef.current, text: text}
    }, [draftCacheRef]);

    React.useEffect(() => {
        const recordChanged = recordIdRef.current !== recordId;

        recordIdRef.current = recordId;

        if (recordChanged) {
            // Approve + "receive next" swaps the record under a mounted card.
            // Kill the pending autosave outright: that draft belongs to the
            // previous patient and must never be written onto this one.
            window.clearTimeout(timerRef.current);
            writeCache(null);
            // nor may an undo bring the previous patient's statement back
            historyRef.current = [];
            lastWasRemovalRef.current = false;
            setCanUndo(false)
        } else if (dirtyRef.current) {
            /*
             * The store caught up with the text we are holding, so it is saved
             * after all — drop the unsaved marker without touching the
             * textarea (it already shows exactly this value).
             *
             * This is the path a sidebar-toggle remount takes: the card comes
             * back with `dirty` seeded true to protect the restored draft, and
             * without this it would stay "unsaved" forever, permanently
             * blocking Approve.
             */
            if ((conclusion || "") === draftRef.current) {
                writeCache(null);
                dirtyRef.current = false;
                setDirty(false)
            }

            // Otherwise the store is echoing something older than what has been
            // typed since the last flush, and overwriting the draft here would
            // silently drop those keystrokes.
            return
        }

        // Text replaced from outside (not our own save echoing back) leaves the
        // removals pointing at lines that are no longer there.
        if ((conclusion || "") !== draftRef.current) {
            historyRef.current = [];
            lastWasRemovalRef.current = false;
            setCanUndo(false)
        }

        // The store now agrees with what was typed (or the record changed), so
        // there is no unsaved draft left to protect.
        writeCache(null);
        setDraft(conclusion || "");
        draftRef.current = conclusion || "";
        dirtyRef.current = false;
        setDirty(false)
    }, [conclusion, recordId, writeCache]);

    React.useEffect(() => {
        setInterpretationDraft(interpretation || "")
    }, [interpretation]);

    const flush = React.useCallback(() => {
        window.clearTimeout(timerRef.current);

        if (!dirtyRef.current || !editableRef.current) {
            return
        }

        dirtyRef.current = false;
        setDirty(false);
        updateConclusionRef.current(draftRef.current)
    }, []);

    const flushInterpretation = React.useCallback(() => {
        if (!editableRef.current || interpretationDraftRef.current === interpretationRef.current) {
            return
        }

        updateInterpretationRef.current(interpretationDraftRef.current)
    }, []);

    const flushAll = React.useCallback(() => {
        flush();
        flushInterpretation()
    }, [flush, flushInterpretation]);
    React.useEffect(() => () => flushAll(), [flushAll]);

    const phrases = React.useMemo(() => OPTIONS.map(option => t(option.phraseKey)), [t]);

    const pick = React.useCallback((index: number) => {
        if (!editableRef.current) {
            return
        }

        const phrase = phrases[index - 1];

        if (typeof phrase === "undefined") {
            return
        }

        setInterpretationDraft(phrase);
        updateInterpretation(phrase)
    }, [phrases, updateInterpretation]);

    // Refreshed on every render (no dependency array) so the page always calls
    // the current closures.
    React.useEffect(() => {
        apiRef.current = {saveNow: flushAll, pick};

        return () => {
            apiRef.current = null
        }
    });

    function closeSuggestions() {
        setSuggestions([]);
        setActiveSuggestion(0)
    }

    /**
     * Show or hide the lead chips for wherever the caret now is.
     *
     * Driven by the TEXT under the caret rather than by what was last
     * inserted, so the chips are equally available on a line the algorithm
     * prefilled and on one typed by hand — click into "ST elevation in II" and
     * they appear, with II already lit.
     *
     * @param value
     * @param caret
     */
    function refreshLeadLine(value: string, caret: number) {
        setLeadLine(editableRef.current ? leadLineAt(value, caret, language) : null)
    }

    /**
     * Add or remove one lead on the current line.
     *
     * @param lead
     */
    function toggleLead(lead: string) {
        const element = textareaRef.current;

        if (element === null || leadLine === null || !editableRef.current) {
            return
        }

        // re-read the line from the field rather than trusting state: the
        // reviewer may have typed since it was last measured
        const line = leadLineAt(element.value, element.selectionStart, language);

        if (line === null) {
            setLeadLine(null);

            return
        }

        /*
         * A line the chips wrote is rebuilt, which is what makes a second tap
         * remove a lead and keeps the order canonical. A line holding anything
         * else — a range like "V2-V4", a note — is only appended to, because
         * rebuilding it from the names the chips recognise would delete what
         * the reviewer typed.
         */
        const result = line.recognised
            ? setLeads(
                element.value,
                line,
                line.leads.indexOf(lead) !== -1
                    ? line.leads.filter(current => current !== lead)
                    : line.leads.concat([lead]),
                CONCLUSION_MAX_LENGTH,
            )
            : appendLead(element.value, line, lead, CONCLUSION_MAX_LENGTH);

        if (result === null) {
            return
        }

        applyDraft(result.value);
        pendingCaretRef.current = result.caret;
        setLeadLine(leadLineAt(result.value, result.caret, language))
    }

    /** Close the line off and move on, the way Enter does after a statement. */
    function finishLeadLine() {
        const element = textareaRef.current;

        if (element === null) {
            return
        }

        const caret = element.selectionStart;
        const line = lineAt(element.value, caret);
        const atEnd = line.end;
        const value = element.value.slice(0, atEnd) + "\n" + element.value.slice(atEnd);

        if (value.length > CONCLUSION_MAX_LENGTH) {
            setLeadLine(null);

            return
        }

        applyDraft(value);
        pendingCaretRef.current = atEnd + 1;
        setLeadLine(null)
    }

    /**
     * Recompute what the caret's line matches.
     *
     * Only the line under the caret is considered — see the note in
     * ./ecgStatements on why the findings box is line-per-statement.
     *
     * @param value the field as it now reads
     * @param caret selectionStart
     */
    function refreshSuggestions(value: string, caret: number) {
        if (!editableRef.current) {
            return
        }

        const line = lineAt(value, caret).text;

        if (dismissedLineRef.current !== null && dismissedLineRef.current === line.trim()) {
            closeSuggestions();

            return
        }

        dismissedLineRef.current = null;

        const matches = matchStatements(line, language);

        setSuggestions(matches);
        setActiveSuggestion(0)
    }

    /**
     * Write a statement over the line being typed.
     *
     * @param statement
     */
    function acceptStatement(statement: EcgStatement) {
        const element = textareaRef.current;

        if (element === null || !editableRef.current) {
            return
        }

        const result = insertStatement(
            element.value,
            element.selectionStart,
            statement,
            language,
            CONCLUSION_MAX_LENGTH,
        );

        // null means the field's character limit would be passed; leaving the
        // text untouched is better than silently truncating a finding
        if (result === null) {
            closeSuggestions();

            return
        }

        applyDraft(result.value);
        pendingCaretRef.current = result.caret;
        dismissedLineRef.current = null;
        closeSuggestions()
    }

    /**
     * Append a statement chosen from the palette.
     *
     * The palette is a browsing gesture, so it always adds to the END of the
     * findings rather than wherever the caret was last left — the caret may be
     * minutes stale by the time someone has searched the list, and inserting
     * into the middle of an earlier line would be a surprise.
     *
     * @param statement
     */
    function insertFromPalette(statement: EcgStatement) {
        if (!editableRef.current) {
            return
        }

        const current = draftRef.current;
        const base = current === "" || current.endsWith("\n") ? current : current + "\n";
        const result = insertStatement(base, base.length, statement, language, CONCLUSION_MAX_LENGTH);

        if (result === null) {
            return
        }

        applyDraft(result.value);
        pendingCaretRef.current = result.caret
    }

    /**
     * Keys the suggestion list owns while it is open.
     *
     * Everything here is swallowed only when there is a list to act on, so with
     * the list closed the textarea behaves exactly as it always did — Enter
     * still makes a new line.
     *
     * @param event
     */
    function handleTextareaKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
        if (suggestions.length === 0) {
            return
        }

        if (event.key === "ArrowDown") {
            event.preventDefault();
            setActiveSuggestion(index => (index + 1) % suggestions.length);

            return
        }

        if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveSuggestion(index => (index - 1 + suggestions.length) % suggestions.length);

            return
        }

        if (event.key === "Enter" || event.key === "Tab") {
            const match = suggestions[activeSuggestion];

            if (typeof match !== "undefined") {
                event.preventDefault();
                acceptStatement(match.statement)
            }

            return
        }

        if (event.key === "Escape") {
            // The page listens for Escape on `window` and would clear the
            // caliper measurement behind this card. React attaches at the
            // document, so stopping here keeps it from ever reaching that.
            event.preventDefault();
            event.stopPropagation();
            dismissedLineRef.current = lineAt(event.currentTarget.value, event.currentTarget.selectionStart).text.trim();
            closeSuggestions()
        }
    }

    /*
     * Put the caret where the insertion left it.
     *
     * It has to wait for the paint: React has only just written the new value
     * into the textarea, and setting selectionStart before that would land in
     * the old text.
     */
    React.useLayoutEffect(() => {
        const caret = pendingCaretRef.current;
        const element = textareaRef.current;

        if (caret === null || element === null) {
            return
        }

        pendingCaretRef.current = null;

        /*
         * Do NOT take focus while the palette is open.
         *
         * Focusing here unconditionally meant every insertion from the palette
         * pulled focus into the textarea behind it: Escape then went to the
         * page instead of closing the palette, and the next arrow key typed
         * into the findings box rather than moving the palette's highlight —
         * so choosing a second statement by keyboard was impossible. The
         * caret still needs setting, and setSelectionRange does not require
         * focus, so only the focus call is conditional.
         */
        if (!paletteOpen) {
            element.focus()
        }

        element.setSelectionRange(caret, caret)
    });

    /**
     * The one path that changes the findings text.
     *
     * Typing and statement insertion both come through here, so an inserted
     * statement autosaves, caches and gates Approve exactly as a typed one
     * does. Anything that sets `draft` without this would be saved only if the
     * technician happened to type afterwards.
     *
     * @param value
     */
    function applyDraft(value: string) {
        setDraft(value);
        draftRef.current = value;
        dirtyRef.current = true;
        setDirty(true);
        // Deliberately NOT cleared by flush(): the dispatch is only a request,
        // and until the store echoes the value back the text is still the only
        // copy that exists.
        writeCache(value);

        window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(flush, AUTOSAVE_DELAY)
    }

    function handleDraftChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
        const value = event.target.value;

        lastWasRemovalRef.current = false;

        applyDraft(value);
        refreshSuggestions(value, event.target.selectionStart)
    }

    /**
     * Take one statement line out of the findings, keeping it for undo.
     *
     * @param index the line's position in the text
     */
    function removeStatement(index: number) {
        if (!editableRef.current) {
            return
        }

        const text = draftRef.current.split("\n")[index];

        if (typeof text === "undefined" || text.trim() === "") {
            return
        }

        historyRef.current.push({text: text, index: index});

        if (historyRef.current.length > UNDO_DEPTH) {
            historyRef.current.shift()
        }

        setCanUndo(true);
        closeSuggestions();
        setLeadLine(null);
        setHoverLine(null);
        applyDraft(removeLine(draftRef.current, index));
        lastWasRemovalRef.current = true
    }

    /** Put the last removed statement back on its line. */
    function undo() {
        const last = historyRef.current.pop();

        if (typeof last === "undefined" || !editableRef.current) {
            return
        }

        setCanUndo(historyRef.current.length > 0);
        applyDraft(insertLine(draftRef.current, last.index, last.text));
        lastWasRemovalRef.current = historyRef.current.length > 0
    }

    // Read by the window listener below, which is attached once.
    const undoRef = React.useRef(undo);
    const paletteOpenRef = React.useRef(paletteOpen);

    undoRef.current = undo;
    paletteOpenRef.current = paletteOpen;

    /*
     * Ctrl+Z (Cmd+Z on a Mac) undoes a removal, from anywhere on the record
     * page. In the findings box itself it does so only straight after a
     * removal; once something has been typed there it is the browser's undo
     * of the typing. Any other field with text keeps its own undo, and the
     * statement list — a dialog over the page — is left alone.
     */
    React.useEffect(() => {
        function onKeyDown(event: KeyboardEvent) {
            const isUndo = (event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey
                && (event.key === "z" || event.key === "Z");

            if (!isUndo || paletteOpenRef.current || historyRef.current.length === 0) {
                return
            }

            const target = event.target as HTMLElement | null;

            if (target !== null && target === textareaRef.current) {
                if (!lastWasRemovalRef.current) {
                    return
                }
            } else {
                const tag = target && target.tagName ? target.tagName.toUpperCase() : "";

                if ((tag === "INPUT" || tag === "TEXTAREA") && (target as HTMLInputElement).value !== "") {
                    return
                }

                if (target && target.isContentEditable) {
                    return
                }
            }

            event.preventDefault();
            undoRef.current()
        }

        window.addEventListener("keydown", onKeyDown);

        return () => window.removeEventListener("keydown", onKeyDown)
    }, []);

    const busy = dirty || updatingConclusion;

    // Gates the page's Approve buttons — see the unsaved-edit note in
    // ModernRecordPage. Reported only while the card is actually editable, so
    // the read-only preview route never blocks anything.
    React.useEffect(() => {
        if (!onBusyChange) {
            return () => undefined
        }

        onBusyChange(editable && busy);

        return () => onBusyChange(false)
    }, [editable, busy, onBusyChange]);

    /*
     * Tell the page, and tell it again on the way out: the band's height cap
     * belongs to the page, and leaving it raised after this card unmounts —
     * which toggling the sidebar does — would keep the trace short for a card
     * that is no longer there.
     */
    React.useEffect(() => {
        if (!onExpandChange) {
            return () => undefined
        }

        onExpandChange(expanded);

        return () => onExpandChange(false)
    }, [expanded, onExpandChange]);

    /*
     * The same fit-to-content machinery mobile already uses. On a phone the
     * field always grows because the page scrolls anyway; on a desktop it grows
     * only while expanded.
     */
    const autoGrow = (mobile && !isDock) || expanded;
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
    const fittedRef = React.useRef<{width: number, text: string} | null>(null);

    const fitTextarea = React.useCallback(() => {
        const el = textareaRef.current;

        if (el === null) {
            return
        }

        if (!autoGrow) {
            el.style.height = "";
            fittedRef.current = null;

            return
        }

        if (el.offsetParent === null) {
            return
        }

        const fitted = fittedRef.current;

        if (fitted !== null && fitted.width === el.clientWidth && fitted.text === el.value) {
            return
        }

        el.style.height = "auto";
        el.style.height = `${el.scrollHeight + (el.offsetHeight - el.clientHeight)}px`;
        fittedRef.current = {width: el.clientWidth, text: el.value}
    }, [autoGrow]);

    React.useLayoutEffect(fitTextarea, [fitTextarea, draft]);
    React.useLayoutEffect(() => {
        const el = textareaRef.current;

        if (el === null || !autoGrow || typeof ResizeObserver === "undefined") {
            return () => undefined
        }

        const observer = new ResizeObserver(() => fitTextarea());

        observer.observe(el);

        return () => observer.disconnect()
    }, [autoGrow, fitTextarea]);

    /*
     * Where each statement sits in the textarea, so its remove button can be
     * laid right after the end of its text and its highlight over its lines.
     *
     * A hidden copy of the textarea (same width, padding, font and wrapping)
     * holds one block per line, its text in a span; the span's boxes give
     * where the statement starts, where it ends, and how far its last line
     * runs. Re-measured on every change, scroll and resize.
     */
    const measureLines = React.useCallback(() => {
        const element = textareaRef.current;
        const mirror = mirrorRef.current;

        if (element === null || mirror === null || !editableRef.current || element.offsetParent === null) {
            setLineMarks(current => current.length === 0 ? current : []);

            return
        }

        const css = window.getComputedStyle(element);

        mirror.style.width = `${element.clientWidth}px`;
        mirror.style.font = css.font;
        mirror.style.letterSpacing = css.letterSpacing;
        mirror.style.lineHeight = css.lineHeight;
        mirror.style.padding = css.padding;

        const lines = element.value.split("\n");

        mirror.textContent = "";
        lines.forEach(line => {
            const block = document.createElement("div");
            const span = document.createElement("span");

            // a zero-width space keeps an empty line one line tall
            span.textContent = line === "" ? "\u200b" : line;
            block.appendChild(span);
            mirror.appendChild(block)
        });

        const spans = Array.prototype.slice.call(mirror.querySelectorAll("span")) as HTMLElement[];
        const height = parseFloat(css.lineHeight) || 18;
        const mirrorBox = mirror.getBoundingClientRect();
        // the mirror has no border; the textarea's text starts inside its own
        const originX = element.offsetLeft + element.clientLeft - mirrorBox.left;
        const originY = element.offsetTop + element.clientTop - element.scrollTop - mirrorBox.top;
        const visibleTop = element.offsetTop + element.clientTop;
        const visibleBottom = visibleTop + element.clientHeight;
        const rightLimit = element.offsetLeft + element.clientLeft + element.clientWidth - 22;
        const expandButton = expandRef.current;
        const marks: LineMark[] = [];

        lines.forEach((line, index) => {
            if (line.trim() === "") {
                return
            }

            const boxes = spans[index].getClientRects();

            if (boxes.length === 0) {
                return
            }

            const first = boxes[0];
            const last = boxes[boxes.length - 1];
            const top = first.top + originY;
            const bottom = last.bottom + originY;
            const lastTop = last.top + originY;
            const buttonTop = lastTop + (height - 18) / 2;
            let left = Math.min(last.right + originX + 4, rightLimit);

            // Where Expand floats over the text, the button moves to its left
            // rather than hiding under it.
            if (expandButton !== null
                && buttonTop + 18 > expandButton.offsetTop
                && left + 20 > expandButton.offsetLeft) {
                left = expandButton.offsetLeft - 24
            }

            // A statement whose last line is at least half in view gets a
            // button: a box squeezed by the Leads row cuts its last line short
            // by a few pixels, and that statement still needs one.
            const middle = lastTop + height / 2;

            if (middle >= visibleTop && middle <= visibleBottom) {
                marks.push({index: index, top: top, bottom: bottom, left: left, buttonTop: buttonTop})
            }
        });

        setLineMarks(marks)
    }, []);

    React.useLayoutEffect(measureLines, [measureLines, draft, expanded, autoGrow, editable, suggestions.length]);
    React.useEffect(() => {
        const element = textareaRef.current;

        if (element === null || typeof ResizeObserver === "undefined") {
            return () => undefined
        }

        const observer = new ResizeObserver(() => measureLines());

        observer.observe(element);

        return () => observer.disconnect()
    }, [measureLines]);

    /**
     * Which statement the pointer is over — on its text or on its button — so
     * the statement is highlighted and its button lights up.
     *
     * @param event
     */
    function trackHover(event: React.MouseEvent<HTMLDivElement>) {
        const y = event.clientY - event.currentTarget.getBoundingClientRect().top;
        const mark = lineMarks.find(candidate => y >= candidate.top && y < candidate.bottom);

        setHoverLine(mark ? mark.index : null)
    }

    const hoverMark = lineMarks.find(mark => mark.index === hoverLine);
    const textBox = textareaRef.current;

    function renderFindings() {
        return (
            <React.Fragment>
                <div className={styles.sect_head}>
                    <span className={styles.microlabel}>{t("modern.interpretation")}</span>
                    <div className={styles.sect_head_actions}>
                        {/* The undo arrow reviewers know from Word: always there, greyed out until there is a removal to undo. */}
                        {editable && (
                            <button
                                type="button"
                                className={styles.undo_button}
                                disabled={!canUndo}
                                title={t("statements.undo_hint")}
                                aria-label={t("statements.undo")}
                                // keeps the caret where the reviewer was typing
                                onMouseDown={event => event.preventDefault()}
                                onClick={undo}
                            >
                                {/* Word's own undo arrow: Microsoft Fluent UI System Icons,
                                  * "arrow_undo_20_regular", MIT licence, (c) Microsoft Corporation */}
                                <svg viewBox="0 0 20 20" aria-hidden="true">
                                    <path d="M5 2.5a.5.5 0 0 0-1 0v4.9c0 .33.27.6.6.6h4.9a.5.5 0 0 0 0-1H5.9l3.48-3.02a4 4 0 0 1 5.25 6.04l-8.17 7.1a.5.5 0 0 0 .65.76l8.17-7.1a5 5 0 0 0-6.56-7.55L5 6.46z"/>
                                </svg>
                            </button>
                        )}
                        {editable && (
                            <button
                                type="button"
                                className={styles.statements_button}
                                onClick={() => setPaletteOpen(true)}
                            >
                                {t("statements.button")}
                            </button>
                        )}
                        {/* nothing autosaves on the read-only preview route, so a save chip there would be noise */}
                        {editable && (
                            <span className={`${styles.savestate} ${busy ? styles.savestate__dirty : ""}`}>
                                {busy ? `● ${t("modern.saving")}` : `✓ ${t("modern.saved")}`}
                            </span>
                        )}
                    </div>
                </div>

                <div
                    className={styles.field}
                    onMouseMove={editable ? trackHover : undefined}
                    onMouseLeave={() => setHoverLine(null)}
                >
                    <textarea
                        ref={textareaRef}
                        className={
                            `${styles.textarea}` +
                            `${isDock ? " " + styles.textarea__dock : ""}` +
                            `${autoGrow ? " " + styles.textarea__grow : ""}` +
                            // declared after __grow in styles.ts, which is what
                            // lets its capped, scrollable overflow win over
                            // that one's hidden overflow
                            `${expanded ? " " + styles.textarea__expanded : ""}` +
                            // room after the last word of a line for its remove button
                            `${editable ? " " + styles.textarea__marks : ""}`
                        }
                        value={draft}
                        disabled={!editable}
                        maxLength={CONCLUSION_MAX_LENGTH}
                        aria-label={t("records.conclusion")}
                        // a combobox that writes into the field it filters
                        role="combobox"
                        aria-expanded={suggestions.length > 0}
                        aria-controls={SUGGESTIONS_ID}
                        aria-autocomplete="list"
                        aria-activedescendant={
                            suggestions.length > 0 ? `${SUGGESTIONS_ID}-option-${activeSuggestion}` : undefined
                        }
                        onChange={handleDraftChange}
                        onKeyDown={handleTextareaKeyDown}
                        // a click or a caret jump means the technician is no
                        // longer completing the word the list was built from
                        onClick={event => {
                            closeSuggestions();
                            refreshLeadLine(event.currentTarget.value, event.currentTarget.selectionStart)
                        }}
                        onKeyUp={event => refreshLeadLine(event.currentTarget.value, event.currentTarget.selectionStart)}
                        onBlur={() => {
                            flush();
                            closeSuggestions()
                        }}
                        onScroll={measureLines}
                    />

                    {/* Hidden copy of the textarea, measured to place the remove buttons. */}
                    {editable && <div ref={mirrorRef} className={styles.mirror} aria-hidden="true"/>}

                    {/* The statement the pointer is on, shaded so it is plain
                      * which one its remove button takes out. */}
                    {hoverMark !== undefined && textBox !== null && (
                        <div
                            className={styles.highlight}
                            style={{
                                top: Math.max(hoverMark.top, textBox.offsetTop + textBox.clientTop),
                                height: Math.min(hoverMark.bottom, textBox.offsetTop + textBox.clientTop + textBox.clientHeight)
                                    - Math.max(hoverMark.top, textBox.offsetTop + textBox.clientTop),
                                left: textBox.offsetLeft + textBox.clientLeft,
                                width: textBox.clientWidth,
                            }}
                            aria-hidden="true"
                        />
                    )}

                    {/* One remove button per statement, right after its text. */}
                    {lineMarks.map(mark => (
                        <button
                            key={mark.index}
                            type="button"
                            tabIndex={-1}
                            className={`${styles.remove} ${hoverLine === mark.index ? styles.remove__hover : ""}`}
                            style={{top: mark.buttonTop, left: mark.left}}
                            title={t("statements.remove")}
                            aria-label={t("statements.remove")}
                            // keeps the caret and the open suggestions where they are
                            onMouseDown={event => event.preventDefault()}
                            onClick={() => removeStatement(mark.index)}
                        >
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M6 6l12 12M18 6L6 18"/>
                            </svg>
                        </button>
                    ))}

                    <StatementSuggestions
                        id={SUGGESTIONS_ID}
                        matches={suggestions}
                        activeIndex={activeSuggestion}
                        onPick={match => acceptStatement(match.statement)}
                        onHover={setActiveSuggestion}
                    />

                    {/* Grows the field in place to show the whole report —
                      * NOT the statement catalogue, which the header button
                      * already offers. The field is about three lines tall,
                      * which suits writing and not reading back a report with
                      * half a dozen findings in it.
                      *
                      * Hidden while the typeahead is open: that list docks to
                      * this same corner, and a button floating over it would
                      * cover the suggestion it sits on. */}
                    {suggestions.length === 0 && draft.trim() !== "" && (
                        <button
                            type="button"
                            ref={expandRef}
                            className={`${styles.expand} ${expanded ? styles.expand__on : ""}`}
                            title={expanded ? t("modern.findings_less") : t("modern.findings_all")}
                            aria-label={expanded ? t("modern.findings_less") : t("modern.findings_all")}
                            aria-expanded={expanded}
                            // keeps the caret in the field, so expanding does
                            // not move where the next statement would land
                            onMouseDown={event => event.preventDefault()}
                            onClick={() => setExpanded(open => !open)}
                        >
                            <span className={styles.expand_glyph}>{expanded ? "⤡" : "⤢"}</span>
                            {expanded ? t("modern.findings_short_less") : t("modern.findings_short")}
                        </button>
                    )}
                </div>

                {/* Never beside the suggestion list: a line holding a whole
                  * statement no longer matches anything to suggest, so the two
                  * cannot both apply — but the guard makes that explicit. */}
                {editable && leadLine !== null && suggestions.length === 0 && (
                    <LeadChips line={leadLine} onToggle={toggleLead} onDone={finishLeadLine}/>
                )}
                {editable && (
                    <span className={styles.counter}>{draft.length}/{CONCLUSION_MAX_LENGTH}</span>
                )}
            </React.Fragment>
        )
    }

    function renderPicker() {
        return (
            <div className={isDock ? styles.dispo_column : styles.dispo_row}>
                <span className={`${styles.microlabel} ${styles.dispo_title}`}>{t("modern.conclusion")}</span>

                <div className={styles.interp_grid}>
                    {OPTIONS.map((option, index) => {
                        const phrase = phrases[index];
                        const active = phrase === interpretationDraft;

                        return (
                            <button
                                key={option.phraseKey}
                                type="button"
                                className={
                                    `${styles.interp}` +
                                    `${active ? " " + styles.interp__active : ""}` +
                                    `${active && option.urgent ? " " + styles.interp__urgent : ""}` +
                                    `${editable ? "" : " " + styles.interp__disabled}`
                                }
                                aria-pressed={active}
                                disabled={!editable}
                                // the chip is an abbreviation; the tooltip is the phrase it writes
                                title={phrase}
                                onClick={() => pick(index + 1)}
                            >
                                {!mobile && <Kbd>{index + 1}</Kbd>}
                                <span className={styles.interp_text}>{t(`interpretation.short.${option.shortKey}`)}</span>
                            </button>
                        )
                    })}
                </div>

                <input
                    type="text"
                    className={`${styles.input} ${isDock ? styles.input__dock : ""}`}
                    value={interpretationDraft}
                    disabled={!editable}
                    aria-label={t("modern.conclusion")}
                    placeholder={t("interpretation.placeholder")}
                    onChange={event => setInterpretationDraft(event.target.value)}
                    onBlur={flushInterpretation}
                />
            </div>
        )
    }

    /* Mounted once per card, outside both arrangements, so opening it from
     * either the card or the dock puts the same dialog on screen. */
    const palette = (
        <StatementPalette
            open={paletteOpen}
            language={language}
            value={draft}
            onInsert={insertFromPalette}
            onClose={() => {
                setPaletteOpen(false);

                // hand the caret back to the findings box, at the end of what
                // was just inserted, so typing carries straight on
                const element = textareaRef.current;

                if (element !== null) {
                    const end = element.value.length;

                    element.focus();
                    element.setSelectionRange(end, end);
                    refreshLeadLine(element.value, end)
                }
            }}
        />
    );

    if (isDock) {
        return (
            <div className={styles.dock_body}>
                <div className={styles.dock_findings}>{renderFindings()}</div>
                <div className={styles.dock_dispo}>{renderPicker()}</div>
                {palette}
            </div>
        )
    }

    return (
        <section className={styles.sect}>
            {renderFindings()}
            {renderPicker()}
            {palette}
        </section>
    )
}

export {ModernDispoCard}

export default compose(
    withConclusion,
    withInterpretation,
)(ModernDispoCard) as React.ComponentType<ModernDispoCardOwnProps>
